import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTenantName } from '../../utils/brandingStyles';

// Styles CSS pour les animations
const animationStyles = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @keyframes sparkle {
    0%, 100% { opacity: 0; transform: scale(0.5); }
    50% { opacity: 1; transform: scale(1); }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/**
 * CustomPaymentSuccessView - Page publique de confirmation après paiement via Custom Payment Link
 * 
 * Cette page ne nécessite PAS d'authentification.
 * Elle est affichée après redirection depuis un Payment Link Stripe créé via le BackOffice.
 * 
 * URL attendue : /payment-success/custom?custom_link_id=xxx
 */
const CustomPaymentSuccessView: React.FC = () => {
    const [searchParams] = useSearchParams();
    const customLinkId = searchParams.get('custom_link_id');

    return (
        <>
            <style>{animationStyles}</style>
            <div
                className="d-flex align-items-center justify-content-center"
                style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                }}
            >
                <div
                    className="text-center p-5"
                    style={{
                        maxWidth: '600px',
                        width: '90%',
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '24px',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        animation: 'fadeInUp 0.6s ease-out',
                    }}
                >
                    {/* Icône de succès */}
                    <div className="mb-4 position-relative d-inline-block">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                            style={{
                                width: '100px',
                                height: '100px',
                                background: 'linear-gradient(135deg, #28a745, #20c997)',
                                boxShadow: '0 10px 40px rgba(40, 167, 69, 0.4)',
                                animation: 'pulse 2s infinite',
                            }}
                        >
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        {/* Particules */}
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="position-absolute rounded-circle"
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    background: '#ffd700',
                                    top: `${20 + Math.sin(i) * 35}%`,
                                    left: `${20 + Math.cos(i) * 35}%`,
                                    animation: `sparkle 2s infinite ${i * 0.3}s`,
                                }}
                            />
                        ))}
                    </div>

                    {/* Titre */}
                    <h1
                        className="mb-3"
                        style={{
                            background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: '2.2rem',
                            fontWeight: 'bold',
                        }}
                    >
                        Paiement confirmé !
                    </h1>

                    <p
                        className="mb-4"
                        style={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '1.1rem',
                            lineHeight: '1.7',
                        }}
                    >
                        Votre paiement a été traité avec succès. Vos crédits IA seront ajoutés
                        à votre compte dans les prochaines minutes.
                    </p>

                    {/* Détails */}
                    <div
                        className="p-4 rounded-4 mb-4 text-start"
                        style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                        }}
                    >
                        <div className="d-flex align-items-center mb-3">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#20c997"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="me-3"
                            >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <span style={{ color: 'rgba(255,255,255,0.9)' }}>
                                Paiement Stripe vérifié et sécurisé
                            </span>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#20c997"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="me-3"
                            >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <span style={{ color: 'rgba(255,255,255,0.9)' }}>
                                Crédits IA en cours d'ajout à votre compte
                            </span>
                        </div>
                        <div className="d-flex align-items-center">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#20c997"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="me-3"
                            >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <span style={{ color: 'rgba(255,255,255,0.9)' }}>
                                Un email de confirmation vous sera envoyé
                            </span>
                        </div>
                    </div>

                    {/* Référence */}
                    {customLinkId && (
                        <p className="mb-4" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                            Référence : {customLinkId}
                        </p>
                    )}

                    {/* Bouton retour */}
                    <a
                        href="/"
                        className="btn px-5 py-3 border-0 rounded-pill"
                        style={{
                            background: 'linear-gradient(45deg, #007bff, #0056b3)',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '1rem',
                            textDecoration: 'none',
                            boxShadow: '0 8px 25px rgba(0,123,255,0.3)',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        Accéder à {getTenantName()}
                    </a>

                    {/* Footer sécurité */}
                    <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', marginBottom: 0 }}>
                            🔒 Transaction sécurisée par Stripe
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomPaymentSuccessView;
